import React from 'react'
import { useTranslation } from '../../../../../shared/hooks/useIntractionTranslate'
import { useDroppable } from '@dnd-kit/core'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../../../constants/colors'
import { ParticipantBoxInfo } from '../ParticipantBoxInfo/ParticipantBoxInfo'
import { Participant } from '../../types'
import { ParticipantsContainerMode } from '../ParticipantsContainer/ParticipantsContainer'

export interface UnassignedParticipantContainerProps {
  participants: Participant[]
  onRemoveParticipant: (id: string) => void
  onDropParticipant?: (participantId: string) => void
  hasValidationErrors?: boolean
  mode: ParticipantsContainerMode
  className?: string
}

export const UnassignedParticipantContainer: React.FC<UnassignedParticipantContainerProps> = ({
  participants,
  onRemoveParticipant,
  onDropParticipant,
  hasValidationErrors = false,
  mode,
  className = ''
}) => {
  const { t } = useTranslation()

  const { isOver, setNodeRef } = useDroppable({
    id: 'unassigned-participants',
    data: {
      type: 'unassigned',
      onDrop: onDropParticipant
    },
    disabled: mode === 'preview' // Disable drag/drop in preview mode
  })

  return (
    <div
      ref={setNodeRef}
      className={`p-4 rounded-lg border transition-colors ${className}`}
      style={{
        backgroundColor: isOver ? INTERAX_COLORS.heritageBlue : INTERAX_COLORS.heritageBlue40,
        borderColor: INTERAX_COLORS.heritageBlue
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h4
          className="font-semibold"
          style={{
            color: INTERAX_COLORS.white,
            fontFamily: INTERAX_TYPOGRAPHY.headline4.fontFamily,
            fontWeight: INTERAX_TYPOGRAPHY.headline4.fontWeight,
            fontSize: INTERAX_TYPOGRAPHY.headline4.fontSize,
            lineHeight: INTERAX_TYPOGRAPHY.headline4.lineHeight
          }}
        >
          {t('facilitator.trainingSprint.addParticipants.unassigned')}
        </h4>

        <span
          className="px-3 py-1 rounded-lg text-sm font-medium"
          style={{
            backgroundColor: INTERAX_COLORS.heritageBlue,
            color: INTERAX_COLORS.thunderGrey,
            fontFamily: INTERAX_TYPOGRAPHY.body3.fontFamily,
            fontWeight: INTERAX_TYPOGRAPHY.body3.fontWeight,
            fontSize: INTERAX_TYPOGRAPHY.body3.fontSize,
            lineHeight: INTERAX_TYPOGRAPHY.body3.lineHeight
          }}
        >
          {participants.length}
        </span>
      </div>

      {/* Participants list */}
      {participants.length === 0 ? (
        <div
          className="text-center py-8"
          style={{
            color: INTERAX_COLORS.thunderGrey,
            fontFamily: INTERAX_TYPOGRAPHY.body2.fontFamily,
            fontWeight: INTERAX_TYPOGRAPHY.body2.fontWeight,
            fontSize: INTERAX_TYPOGRAPHY.body2.fontSize,
            lineHeight: INTERAX_TYPOGRAPHY.body2.lineHeight
          }}
        >
          {t('facilitator.trainingSprint.addParticipants.noUnassignedParticipants')}
        </div>
      ) : (
        <div className="space-y-2">
          {participants.map((participant) => (
            <ParticipantBoxInfo
              key={participant.id}
              participant={participant}
              onRemove={onRemoveParticipant}
              hasError={hasValidationErrors}
              mode={mode}
            />
          ))}
        </div>
      )}
    </div>
  )
}
